import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookText, Building2, CircleHelp, FileText, Landmark, LayoutDashboard, RefreshCcw, ShieldCheck, Users } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import RecordTag from "@/components/RecordTag";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { adminApi } from "@/lib/api";

const tabs = [
  { id: "overview", label: "Überblick", icon: LayoutDashboard },
  { id: "applications", label: "Anträge", icon: ShieldCheck },
  { id: "members", label: "Mitglieder", icon: Users },
  { id: "payments", label: "Zahlungen", icon: Landmark },
  { id: "tickets", label: "Vorgänge", icon: FileText },
  { id: "legal", label: "Rechtliches", icon: BookText },
  { id: "help", label: "Hilfe", icon: CircleHelp },
  { id: "mailbox", label: "Posteingang", icon: FileText },
  { id: "content", label: "Inhalte & Module", icon: Building2 },
];

const metricLabels = {
  members: "Mitglieder",
  pending_applications: "Offene Anträge",
  open_tickets: "Offene Vorgänge",
  due_payments: "Fällige Beiträge",
};

function SectionBanner({ kicker, title, description, testId }) {
  return (
    <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={testId}>
      <CardContent className="space-y-3 p-6">
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8a6f3b]">{kicker}</p>
        <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">{title}</h2>
        <p className="text-sm leading-relaxed text-slate-600">{description}</p>
      </CardContent>
    </Card>
  );
}

function MetaCell({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-medium leading-relaxed text-slate-900">{value}</p>
    </div>
  );
}

export default function AdminPage({ session, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [mailboxFilter, setMailboxFilter] = useState("all");
  const [mailboxDrafts, setMailboxDrafts] = useState({});
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [legalDrafts, setLegalDrafts] = useState({});
  const [internalGuideDraft, setInternalGuideDraft] = useState({ title: "", content: "" });
  const [selectedMemberId, setSelectedMemberId] = useState(searchParams.get("member") || "");
  const [memberDraft, setMemberDraft] = useState(null);
  const [createdMemberResult, setCreatedMemberResult] = useState(null);
  const [createMemberForm, setCreateMemberForm] = useState({
    full_name: "",
    email: "",
    title: "",
    phone: "",
    city: "",
    website: "",
    bio: "",
    specialties: "",
    member_type: "Ordentliches Mitglied",
    public_profile: true,
    status: "approved",
    badge_status: "REEXPERT",
    fee_status: "mandat liegt vor",
    billing_address: "",
    rexpert_id: "",
    admin_note: "",
  });
  const [seminarForm, setSeminarForm] = useState({
    title: "",
    category: "Digitalisierung & KI",
    hours: "2.0",
    starts_at: "2026-09-25T10:00:00+02:00",
    venue: "Digital-Live-Seminar",
    speaker: "Verbandsteam",
  });

  const loadSnapshot = async () => {
    if (!session?.token || session?.user?.role !== "admin") {
      navigate("/login");
      return;
    }
    try {
      const data = await adminApi.getSnapshot(session.token);
      setSnapshot(data);
      setSettingsDraft(data.settings);
      const nextLegalDrafts = {};
      (data.legal || []).forEach((doc) => {
        nextLegalDrafts[doc.slug] = { title: doc.title, content: doc.content };
      });
      setLegalDrafts(nextLegalDrafts);
      setInternalGuideDraft(data.internal_guide || { title: "", content: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Die Verwaltungsoberfläche konnte nicht geladen werden.");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, [session?.token]);

  useEffect(() => {
    const tab = searchParams.get("tab") || "overview";
    setActiveTab(tab);
    setSelectedMemberId(searchParams.get("member") || "");
  }, [searchParams]);

  const memberUsers = (snapshot?.users || []).filter((user) => user.role === "member");
  const selectedMember = memberUsers.find((user) => user.id === selectedMemberId) || memberUsers[0] || null;
  const selectedApplications = selectedMember ? (snapshot?.applications || []).filter((application) => application.email === selectedMember.email) : [];
  const selectedTickets = selectedMember ? (snapshot?.tickets || []).filter((ticket) => ticket.requester_email === selectedMember.email) : [];
  const selectedPayments = selectedMember ? (snapshot?.payments || []).filter((payment) => payment.email === selectedMember.email) : [];
  const selectedComplaints = selectedMember ? (snapshot?.complaints || []).filter((complaint) => complaint.email === selectedMember.email || complaint.member_reference === selectedMember.rexpert_id) : [];
  const selectedEmailLogs = selectedMember ? (snapshot?.email_logs || []).filter((log) => log.recipient === selectedMember.email) : [];

  useEffect(() => {
    if (!selectedMember) return;
    setMemberDraft({
      full_name: selectedMember.full_name || "",
      title: selectedMember.title || "",
      phone: selectedMember.phone || "",
      city: selectedMember.city || "",
      website: selectedMember.website || "",
      bio: selectedMember.bio || "",
      specialties: (selectedMember.specialties || []).join(", "),
      member_type: selectedMember.member_type || "",
      public_profile: !!selectedMember.public_profile,
      status: selectedMember.status || "",
      badge_status: selectedMember.badge_status || "",
      fee_status: selectedMember.fee_status || "",
      billing_address: selectedMember.billing_address || selectedMember.billing_preferences?.account_address || "",
      rexpert_id: selectedMember.rexpert_id || "",
      admin_note: selectedMember.admin_note || "",
    });
  }, [selectedMember?.id]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    navigate(`/admin?tab=${tabId}`, { replace: true });
  };

  const openMemberDossier = (memberId) => {
    setSelectedMemberId(memberId);
    navigate(`/admin?tab=members&member=${memberId}`, { replace: true });
  };

  const reviewApplication = async (applicationId, decision) => {
    try {
      const response = await adminApi.reviewApplication(session.token, applicationId, {
        decision,
        note: decision === "approve" ? "Antrag fachlich freigegeben" : "Antrag aktuell zurückgestellt",
      });
      toast.success(
        decision === "approve"
          ? `Antrag freigegeben${response.temporary_password ? ` · Startpasswort: ${response.temporary_password}` : ""}`
          : "Antrag abgelehnt"
      );
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Aktion nicht möglich.");
    }
  };

  const openReferenceFile = async (file) => {
    try {
      const blob = await adminApi.downloadFile(session.token, file.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Datei konnte nicht geöffnet werden.");
    }
  };

  const downloadAdminDocument = async (path, filename) => {
    try {
      const blob = await adminApi.downloadAdminDocument(session.token, path);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dokumentenexport gestartet.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Dokumentenexport konnte nicht gestartet werden.");
    }
  };

  const updateTicket = async (ticketId, status) => {
    try {
      await adminApi.updateTicketStatus(session.token, ticketId, { status, note: `Status geändert auf ${status}` });
      toast.success("Vorgangsstatus aktualisiert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Vorgang konnte nicht aktualisiert werden.");
    }
  };

  const saveSettings = async () => {
    try {
      await adminApi.updateSettings(session.token, settingsDraft);
      toast.success("Einstellungen gespeichert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Einstellungen konnten nicht gespeichert werden.");
    }
  };

  const saveLegal = async (slug) => {
    try {
      await adminApi.updateLegal(session.token, slug, legalDrafts[slug]);
      toast.success("Rechtstext aktualisiert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Rechtstext konnte nicht gespeichert werden.");
    }
  };

  const refreshNews = async () => {
    try {
      await adminApi.refreshNews(session.token);
      toast.success("KI-News aktualisiert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "News konnten nicht aktualisiert werden.");
    }
  };

  const createSeminar = async () => {
    try {
      await adminApi.createSeminar(session.token, { ...seminarForm, hours: Number(seminarForm.hours) });
      toast.success("Seminar angelegt.");
      setSeminarForm({
        title: "",
        category: "Digitalisierung & KI",
        hours: "2.0",
        starts_at: "2026-09-25T10:00:00+02:00",
        venue: "Digital-Live-Seminar",
        speaker: "Verbandsteam",
      });
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Seminar konnte nicht angelegt werden.");
    }
  };

  const saveMemberDraft = async () => {
    if (!selectedMember || !memberDraft) return;
    try {
      await adminApi.updateMember(session.token, selectedMember.id, {
        ...memberDraft,
        specialties: memberDraft.specialties.split(",").map((item) => item.trim()).filter(Boolean),
      });
      toast.success("Mitgliedsakte aktualisiert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mitgliedsakte konnte nicht aktualisiert werden.");
    }
  };

  const createMember = async () => {
    try {
      const response = await adminApi.createMember(session.token, {
        ...createMemberForm,
        specialties: createMemberForm.specialties.split(",").map((item) => item.trim()).filter(Boolean),
      });
      setCreatedMemberResult(response);
      toast.success("Mitglied manuell angelegt.");
      await loadSnapshot();
      setCreateMemberForm({
        full_name: "",
        email: "",
        title: "",
        phone: "",
        city: "",
        website: "",
        bio: "",
        specialties: "",
        member_type: "Ordentliches Mitglied",
        public_profile: true,
        status: "approved",
        badge_status: "REEXPERT",
        fee_status: "mandat liegt vor",
        billing_address: "",
        rexpert_id: "",
        admin_note: "",
      });
      if (response?.user?.id) {
        openMemberDossier(response.user.id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mitglied konnte nicht angelegt werden.");
    }
  };

  const triggerMemberReset = async () => {
    if (!selectedMember) return;
    try {
      await adminApi.triggerMemberPasswordReset(session.token, selectedMember.id);
      toast.success("Passwort-Zurücksetzung an das Mitglied versendet.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Passwort-Zurücksetzung konnte nicht angestoßen werden.");
    }
  };

  const updateMandateStatus = async (paymentId, status, paymentStatus) => {
    try {
      await adminApi.updatePaymentStatus(session.token, paymentId, { status, payment_status: paymentStatus, note: `Status gesetzt auf ${status}` });
      toast.success("Mandatsstatus aktualisiert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mandatsstatus konnte nicht aktualisiert werden.");
    }
  };

  const filteredPayments = (snapshot?.payments || []).filter((payment) => {
    if (paymentFilter === "all") return true;
    const status = String(payment.status || "").toLowerCase();
    if (paymentFilter === "open") return status.includes("mandat bestätigt") || status.includes("offen");
    if (paymentFilter === "checked") return status.includes("mandat geprüft") || status.includes("mandat bestätigt");
    if (paymentFilter === "released") return status.includes("abbuchung freigegeben") || String(payment.payment_status || "").toLowerCase().includes("freigegeben");
    return true;
  });

  const filteredMailboxMessages = (snapshot?.mailbox_messages || []).filter((mail) => {
    if (mailboxFilter === "all") return true;
    if (mailboxFilter === "unread") return !!mail.unread;
    if (mailboxFilter === "bounce") return mail.mail_type === "Rückläufer";
    if (mailboxFilter === "member") return ["Mitgliedsantrag", "Beschwerde", "Mandat", "Passwort"].includes(mail.mail_type);
    return true;
  });

  const sortedMailboxMessages = [...filteredMailboxMessages].sort((a, b) => {
    const score = (mail) => {
      if (mail.mail_type === "Rückläufer") return 0;
      if (mail.unread) return 1;
      if (["Mitgliedsantrag", "Beschwerde", "Mandat", "Passwort"].includes(mail.mail_type)) return 2;
      return 3;
    };
    return score(a) - score(b);
  });

  const saveInternalGuide = async () => {
    try {
      await adminApi.updateInternalGuide(session.token, internalGuideDraft);
      toast.success("Interne Hilfe gespeichert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Interne Hilfe konnte nicht gespeichert werden.");
    }
  };

  const saveMailboxAnnotation = async (mailId) => {
    const payload = mailboxDrafts[mailId];
    if (!payload) return;
    try {
      await adminApi.updateMailboxAnnotation(session.token, mailId, payload);
      toast.success("Mail-Vermerk gespeichert.");
      await loadSnapshot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mail-Vermerk konnte nicht gespeichert werden.");
    }
  };

  if (loading || !snapshot || !settingsDraft) {
    return (
      <MainLayout session={session} onLogout={onLogout}>
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="admin-loading-panel">Verwaltungsoberfläche wird geladen ...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Verbandsverwaltung"
      pageDescription="Adminbereich für Mitgliederverwaltung, Vorgänge, Mandate, Ordnungen und Verbandsbetrieb bei REEXPERTS."
    >
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[280px_1fr]">
          <aside className="rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-sm" data-testid="admin-sidebar">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-400" data-testid="admin-sidebar-kicker">Verwaltungsregister</p>
            <h1 className="mt-3 font-serif text-[2rem] tracking-tight" data-testid="admin-sidebar-heading">Zentrale Verbandsverwaltung</h1>
            <div className="mt-4 flex items-center gap-2" data-testid="admin-sidebar-flag-accent">
              <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
              <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
              <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#cdb27f]" data-testid="admin-sidebar-dach-note">Deutschlandfokus · D-A-CH Verwaltung</p>
            <div className="mt-6 space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => switchTab(tab.id)}
                    className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                      activeTab === tab.id ? "bg-[#e5d3b3] text-[#121212]" : "text-slate-300 hover:bg-white/10"
                    }`}
                    data-testid={`admin-tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" /> {tab.label}
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="space-y-8" data-testid="admin-content-area">
            <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-legend-card">
              <CardContent className="grid gap-5 p-5 xl:grid-cols-3">
                <div data-testid="admin-legend-status-block">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Statusfarben</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <StatusBadge status="offen" testId="admin-legend-status-open" />
                    <StatusBadge status="in Prüfung" testId="admin-legend-status-review" />
                    <StatusBadge status="approved" testId="admin-legend-status-approved" />
                    <StatusBadge status="paid" testId="admin-legend-status-paid" />
                  </div>
                </div>

                <div data-testid="admin-legend-record-block">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Kennungen</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <RecordTag value="APP-2026-0001" testId="admin-legend-record-app" />
                    <RecordTag value="TKT-2026-0001" testId="admin-legend-record-ticket" />
                    <RecordTag value="CMP-2026-0001" testId="admin-legend-record-complaint" />
                    <RecordTag value="REEXPERTS-2026-001" testId="admin-legend-record-member" />
                  </div>
                </div>

                <div data-testid="admin-legend-case-block">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Falltypen</p>
                  <ul className="mt-3 space-y-2 text-sm text-slate-600">
                    <li data-testid="admin-legend-case-1">Aufnahmevorgang</li>
                    <li data-testid="admin-legend-case-2">Beschwerdevorgang</li>
                    <li data-testid="admin-legend-case-3">Beitragsvorgang</li>
                    <li data-testid="admin-legend-case-4">Verwaltungsvorgang</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {activeTab === "overview" && (
              <section className="space-y-6" data-testid="admin-overview-panel">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {Object.entries(snapshot.metrics).map(([key, value]) => (
                    <Card key={key} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-metric-${key}`}>
                      <CardContent className="p-5">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{metricLabels[key] || key.replace(/_/g, " ")}</p>
                        <p className="mt-2 text-3xl font-semibold text-slate-950">{value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
                  <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-overview-news-panel">
                    <CardContent className="space-y-4 p-6">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">KI-News</p>
                          <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Branchenhinweise und Lagebilder</h2>
                        </div>
                        <Button onClick={refreshNews} className="rounded-full bg-slate-900 text-white hover:bg-slate-800" data-testid="admin-refresh-news-button">
                          <RefreshCcw className="mr-2 h-4 w-4" /> Aktualisieren
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {snapshot.news.slice(0, 4).map((item) => (
                          <div key={item.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-news-item-${item.id}`}>
                            <p className="font-semibold text-slate-950">{item.headline}</p>
                            <p className="mt-1 text-sm text-slate-600">{item.summary}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-overview-complaints-panel">
                    <CardContent className="space-y-4 p-6">
                      <div>
                        <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">Beschwerdestelle</p>
                        <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Eingegangene Verfahren</h2>
                      </div>
                      <div className="space-y-4">
                        {snapshot.complaints.map((item) => (
                          <div key={item.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-complaint-item-${item.id}`}>
                            <div className="flex items-center justify-between gap-4">
                              <p className="font-semibold text-slate-950">{item.subject}</p>
                              <StatusBadge status={item.status} testId={`admin-complaint-status-${item.id}`} />
                            </div>
                            <p className="mt-2 text-sm text-slate-600">{item.full_name} · {item.role}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-overview-email-panel">
                  <CardContent className="space-y-4 p-6">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">E-Mail-Ausgang</p>
                      <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Zuletzt protokollierte E-Mail-Vorgänge</h2>
                    </div>
                    <div className="space-y-3">
                      {(snapshot.email_logs || []).slice(0, 6).map((mail) => (
                        <div key={mail.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-email-log-${mail.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-950">{mail.subject}</p>
                              <p className="text-sm text-slate-500">{mail.recipient}</p>
                            </div>
                            <StatusBadge status={mail.status} testId={`admin-email-log-status-${mail.id}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-overview-mailbox-panel">
                  <CardContent className="space-y-4 p-6">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                      <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">Posteingang</p>
                      <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Zuletzt empfangene E-Mails</h2>
                      </div>
                      <Button variant="outline" className="rounded-full" onClick={() => switchTab("mailbox")} data-testid="admin-overview-open-mailbox-button">Vollansicht</Button>
                    </div>
                    <div className="flex flex-wrap gap-3" data-testid="admin-overview-mailbox-filter-bar">
                      <Button variant={mailboxFilter === "all" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("all")} data-testid="admin-overview-mailbox-filter-all">Alle</Button>
                      <Button variant={mailboxFilter === "unread" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("unread")} data-testid="admin-overview-mailbox-filter-unread">Ungelesen</Button>
                      <Button variant={mailboxFilter === "bounce" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("bounce")} data-testid="admin-overview-mailbox-filter-bounce">Rückläufer</Button>
                      <Button variant={mailboxFilter === "member" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("member")} data-testid="admin-overview-mailbox-filter-member">Mitgliedsbezogen</Button>
                    </div>
                    <div className="space-y-3">
                      {sortedMailboxMessages.slice(0, 8).map((mail) => (
                        <div key={mail.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-mailbox-item-${mail.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-950">{mail.subject || "Ohne Betreff"}</p>
                              <p className="text-sm text-slate-500">{mail.from}</p>
                              {mail.preview && <p className="mt-2 text-sm leading-relaxed text-slate-600">{mail.preview}</p>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <StatusBadge status={mail.mail_type} testId={`admin-mailbox-type-${mail.id}`} />
                              <StatusBadge status={mail.unread ? "ungelesen" : "gelesen"} testId={`admin-mailbox-status-${mail.id}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {activeTab === "applications" && (
              <section className="space-y-4" data-testid="admin-applications-panel">
                <SectionBanner
                  kicker="Aktenverzeichnis"
                  title="Anträge mit klarer Fallstruktur"
                  description="Aufnahmevorgänge erscheinen mit Kennung, Status, Referenzlage und sofort lesbarer Aktenführung."
                  testId="admin-applications-banner"
                />
                {snapshot.applications.map((application) => (
                  <Card key={application.id} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-application-card-${application.id}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <RecordTag value={application.id} testId={`admin-application-id-${application.id}`} />
                          <h2 className="font-serif text-3xl tracking-tight text-slate-950">{application.full_name}</h2>
                          <p className="text-sm text-slate-500">{application.profession} · {application.city}</p>
                        </div>
                        <StatusBadge status={application.status} testId={`admin-application-status-${application.id}`} />
                      </div>
                      <div className="grid gap-3 md:grid-cols-3">
                        <MetaCell label="Ort" value={application.city} testId={`admin-application-city-${application.id}`} />
                        <MetaCell label="Beruf" value={application.profession} testId={`admin-application-profession-${application.id}`} />
                        <MetaCell label="Referenzen" value={`${application.reference_files?.length || 0} hinterlegt`} testId={`admin-application-references-${application.id}`} />
                      </div>
                      <MetaCell label="Fachgebiete" value={application.specialties?.join(", ") || "Nicht angegeben"} testId={`admin-application-specialties-${application.id}`} />
                      <MetaCell label="Selbstdarstellung" value={application.about} testId={`admin-application-about-${application.id}`} />
                      {!!application.reference_files?.length && (
                        <div className="flex flex-wrap gap-3" data-testid={`admin-application-files-${application.id}`}>
                          {application.reference_files.map((file, index) => (
                            <Button
                              key={file.id}
                              variant="outline"
                              className="rounded-full"
                              onClick={() => openReferenceFile(file)}
                              data-testid={`admin-application-file-open-${application.id}-${index + 1}`}
                            >
                              Referenz {index + 1} öffnen
                            </Button>
                          ))}
                        </div>
                      )}
                      {application.generated_rexpert_id && (
                        <div className="rounded-3xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid={`admin-application-rexpert-box-${application.id}`}>
                          Freigegebene Kennung: {application.generated_rexpert_id}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <Button onClick={() => reviewApplication(application.id, "approve")} className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" data-testid={`admin-application-approve-${application.id}`}>Freigeben</Button>
                        <Button variant="outline" onClick={() => reviewApplication(application.id, "reject")} className="rounded-full" data-testid={`admin-application-reject-${application.id}`}>Ablehnen</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => downloadAdminDocument(`/admin/documents/application/${application.id}/summary/pdf`, `${application.id}-akte.pdf`)} data-testid={`admin-application-summary-pdf-${application.id}`}>Aktenblatt PDF</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => downloadAdminDocument(`/admin/documents/application/${application.id}/summary/html`, `${application.id}-akte.html`)} data-testid={`admin-application-summary-html-${application.id}`}>Aktenblatt HTML</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </section>
            )}

            {activeTab === "members" && (
              <section className="space-y-6" data-testid="admin-members-panel">
                <SectionBanner
                  kicker="Mitgliederregister"
                  title="Mitgliederverwaltung als ausführliche Verwaltungsakte"
                  description="Registerübersicht, Kurzdetails und vollständige Aktenansicht je Mitglied mit Verwaltungs- und Mandatsdaten."
                  testId="admin-members-banner"
                />

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-member-create-card">
                  <CardContent className="space-y-4 p-6">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Mitglied manuell anlegen</p>
                      <h2 className="mt-2 font-serif text-[2rem] tracking-tight text-slate-950">Neues Mitglied erfassen</h2>
                    </div>
                    {createdMemberResult?.temporary_password && (
                      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="admin-created-member-password-box">
                        Startpasswort: <strong>{createdMemberResult.temporary_password}</strong>
                      </div>
                    )}
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <Input value={createMemberForm.full_name} onChange={(event) => setCreateMemberForm({ ...createMemberForm, full_name: event.target.value })} placeholder="Vollständiger Name" data-testid="admin-create-member-full-name-input" />
                      <Input value={createMemberForm.email} onChange={(event) => setCreateMemberForm({ ...createMemberForm, email: event.target.value })} placeholder="E-Mail" data-testid="admin-create-member-email-input" />
                      <Input value={createMemberForm.title} onChange={(event) => setCreateMemberForm({ ...createMemberForm, title: event.target.value })} placeholder="Titel / Berufsbezeichnung" data-testid="admin-create-member-title-input" />
                      <Input value={createMemberForm.phone} onChange={(event) => setCreateMemberForm({ ...createMemberForm, phone: event.target.value })} placeholder="Telefon" data-testid="admin-create-member-phone-input" />
                      <Input value={createMemberForm.city} onChange={(event) => setCreateMemberForm({ ...createMemberForm, city: event.target.value })} placeholder="Ort" data-testid="admin-create-member-city-input" />
                      <Input value={createMemberForm.website} onChange={(event) => setCreateMemberForm({ ...createMemberForm, website: event.target.value })} placeholder="Website" data-testid="admin-create-member-website-input" />
                      <Input value={createMemberForm.specialties} onChange={(event) => setCreateMemberForm({ ...createMemberForm, specialties: event.target.value })} placeholder="Fachgebiete (kommagetrennt)" data-testid="admin-create-member-specialties-input" className="md:col-span-2 xl:col-span-1" />
                      <Input value={createMemberForm.rexpert_id} onChange={(event) => setCreateMemberForm({ ...createMemberForm, rexpert_id: event.target.value })} placeholder="REEXPERTS-ID (optional)" data-testid="admin-create-member-id-input" />
                      <select value={createMemberForm.member_type} onChange={(event) => setCreateMemberForm({ ...createMemberForm, member_type: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-create-member-type-select">
                        <option value="Ordentliches Mitglied">Ordentliches Mitglied</option>
                        <option value="Assoziiertes Mitglied">Assoziiertes Mitglied</option>
                        <option value="Makler mit Weiterbildung">Makler mit Sachverständigen-Weiterbildung</option>
                      </select>
                      <select value={createMemberForm.status} onChange={(event) => setCreateMemberForm({ ...createMemberForm, status: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-create-member-status-select">
                        <option value="approved">approved</option>
                        <option value="active">active</option>
                        <option value="ruhend">ruhend</option>
                        <option value="gesperrt">gesperrt</option>
                      </select>
                      <select value={createMemberForm.fee_status} onChange={(event) => setCreateMemberForm({ ...createMemberForm, fee_status: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-create-member-fee-status-select">
                        <option value="mandat liegt vor">mandat liegt vor</option>
                        <option value="Mandat geprüft">Mandat geprüft</option>
                        <option value="Mandat bestätigt">Mandat bestätigt</option>
                        <option value="Abbuchung freigegeben">Abbuchung freigegeben</option>
                      </select>
                      <label className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-create-member-public-checkbox-box">
                        <input type="checkbox" checked={createMemberForm.public_profile} onChange={(event) => setCreateMemberForm({ ...createMemberForm, public_profile: event.target.checked })} data-testid="admin-create-member-public-checkbox" />
                        Öffentlich sichtbar
                      </label>
                    </div>
                    <Textarea value={createMemberForm.bio} onChange={(event) => setCreateMemberForm({ ...createMemberForm, bio: event.target.value })} placeholder="Kurzprofil / fachliche Einordnung" data-testid="admin-create-member-bio-textarea" />
                    <Textarea value={createMemberForm.billing_address} onChange={(event) => setCreateMemberForm({ ...createMemberForm, billing_address: event.target.value })} placeholder="Rechnungsanschrift" data-testid="admin-create-member-billing-textarea" />
                    <Textarea value={createMemberForm.admin_note} onChange={(event) => setCreateMemberForm({ ...createMemberForm, admin_note: event.target.value })} placeholder="Interne Notiz / Prüfvermerk" data-testid="admin-create-member-note-textarea" />
                    <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={createMember} data-testid="admin-create-member-save-button">Mitglied anlegen</Button>
                  </CardContent>
                </Card>

                <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
                  <div className="space-y-4" data-testid="admin-member-register-column">
                    {memberUsers.map((user) => (
                      <Card key={user.id} className={`border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm ${selectedMember?.id === user.id ? 'ring-2 ring-[#d4af37]/50' : ''}`} data-testid={`admin-member-card-${user.id}`}>
                        <CardContent className="space-y-3 p-5">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="font-semibold text-slate-950">{user.full_name}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                            <StatusBadge status={user.badge_status || user.status} testId={`admin-member-status-${user.id}`} />
                          </div>
                          <div className="grid gap-3">
                            <MetaCell label="Kennung" value={user.rexpert_id || "noch offen"} testId={`admin-member-id-${user.id}`} />
                            <MetaCell label="Standort" value={user.city} testId={`admin-member-city-${user.id}`} />
                          </div>
                          <div className="flex flex-wrap gap-3 pt-2">
                            <Button variant="outline" className="rounded-full" onClick={() => openMemberDossier(user.id)} data-testid={`admin-member-open-dossier-${user.id}`}>Akte öffnen</Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {selectedMember && memberDraft && (
                    <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-member-dossier-card">
                      <CardContent className="space-y-6 p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Mitgliederakte</p>
                            <h2 className="mt-2 font-serif text-[2rem] tracking-tight text-slate-950">{selectedMember.full_name}</h2>
                            <p className="text-sm text-slate-500">{selectedMember.email}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <RecordTag value={selectedMember.rexpert_id || "noch offen"} testId="admin-member-dossier-id" />
                            <StatusBadge status={selectedMember.badge_status || selectedMember.status} testId="admin-member-dossier-status" />
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-3">
                          <MetaCell label="Mitglied seit" value={selectedMember.created_at || "nicht hinterlegt"} testId="admin-member-dossier-since" />
                          <MetaCell label="Mitgliedsart" value={selectedMember.member_type || "nicht hinterlegt"} testId="admin-member-dossier-type" />
                          <MetaCell label="Beitragsstatus" value={selectedMember.fee_status || "nicht hinterlegt"} testId="admin-member-dossier-fee-status" />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2" data-testid="admin-member-dossier-edit-grid">
                          <Input value={memberDraft.full_name} onChange={(event) => setMemberDraft({ ...memberDraft, full_name: event.target.value })} placeholder="Vollständiger Name" data-testid="admin-member-edit-full-name" />
                          <Input value={memberDraft.title} onChange={(event) => setMemberDraft({ ...memberDraft, title: event.target.value })} placeholder="Titel / Berufsbezeichnung" data-testid="admin-member-edit-title" />
                          <Input value={selectedMember.email} readOnly data-testid="admin-member-edit-email" />
                          <Input value={memberDraft.phone} onChange={(event) => setMemberDraft({ ...memberDraft, phone: event.target.value })} placeholder="Telefon" data-testid="admin-member-edit-phone" />
                          <Input value={memberDraft.city} onChange={(event) => setMemberDraft({ ...memberDraft, city: event.target.value })} placeholder="Ort" data-testid="admin-member-edit-city" />
                          <Input value={memberDraft.website} onChange={(event) => setMemberDraft({ ...memberDraft, website: event.target.value })} placeholder="Website" data-testid="admin-member-edit-website" />
                          <Input value={memberDraft.rexpert_id} onChange={(event) => setMemberDraft({ ...memberDraft, rexpert_id: event.target.value })} placeholder="REEXPERTS-ID" data-testid="admin-member-edit-rexpert-id" />
                          <Input value={memberDraft.specialties} onChange={(event) => setMemberDraft({ ...memberDraft, specialties: event.target.value })} placeholder="Fachgebiete (kommagetrennt)" data-testid="admin-member-edit-specialties" />
                          <select value={memberDraft.member_type} onChange={(event) => setMemberDraft({ ...memberDraft, member_type: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-member-edit-member-type">
                            <option value="Ordentliches Mitglied">Ordentliches Mitglied</option>
                            <option value="Assoziiertes Mitglied">Assoziiertes Mitglied</option>
                            <option value="Makler mit Weiterbildung">Makler mit Sachverständigen-Weiterbildung</option>
                          </select>
                          <select value={memberDraft.status} onChange={(event) => setMemberDraft({ ...memberDraft, status: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-member-edit-status">
                            <option value="approved">approved</option>
                            <option value="active">active</option>
                            <option value="ruhend">ruhend</option>
                            <option value="gesperrt">gesperrt</option>
                          </select>
                          <select value={memberDraft.badge_status} onChange={(event) => setMemberDraft({ ...memberDraft, badge_status: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-member-edit-badge-status">
                            <option value="REEXPERT">REEXPERT</option>
                            <option value="ruhend">ruhend</option>
                            <option value="gesperrt">gesperrt</option>
                          </select>
                          <select value={memberDraft.fee_status} onChange={(event) => setMemberDraft({ ...memberDraft, fee_status: event.target.value })} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-member-edit-fee-status">
                            <option value="mandat liegt vor">mandat liegt vor</option>
                            <option value="Mandat geprüft">Mandat geprüft</option>
                            <option value="Mandat bestätigt">Mandat bestätigt</option>
                            <option value="Abbuchung freigegeben">Abbuchung freigegeben</option>
                            <option value="paid">paid</option>
                          </select>
                          <label className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="admin-member-edit-public-box">
                            <input type="checkbox" checked={memberDraft.public_profile} onChange={(event) => setMemberDraft({ ...memberDraft, public_profile: event.target.checked })} data-testid="admin-member-edit-public" />
                            Öffentlich sichtbar
                          </label>
                        </div>

                        <Textarea value={memberDraft.bio} onChange={(event) => setMemberDraft({ ...memberDraft, bio: event.target.value })} placeholder="Kurzprofil" data-testid="admin-member-edit-bio" />
                        <Textarea value={memberDraft.billing_address} onChange={(event) => setMemberDraft({ ...memberDraft, billing_address: event.target.value })} placeholder="Rechnungsanschrift" data-testid="admin-member-edit-billing-address" />
                        <Textarea value={memberDraft.admin_note} onChange={(event) => setMemberDraft({ ...memberDraft, admin_note: event.target.value })} placeholder="Interner Verwaltungsvermerk" data-testid="admin-member-edit-admin-note" />

                        <div className="flex flex-wrap gap-3">
                          <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={saveMemberDraft} data-testid="admin-member-save-button">Mitgliedsakte speichern</Button>
                          <Button variant="outline" className="rounded-full" onClick={triggerMemberReset} data-testid="admin-member-password-reset-button">Passwort-Reset senden</Button>
                          <Button variant="outline" className="rounded-full" onClick={() => downloadAdminDocument(`/admin/documents/member/${selectedMember.id}/certificate/pdf`, `${selectedMember.rexpert_id || selectedMember.id}-mitgliedsbescheinigung.pdf`)} data-testid="admin-member-dossier-certificate-pdf">Mitgliedsbescheinigung PDF</Button>
                        </div>

                        <div className="grid gap-4 lg:grid-cols-2">
                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-mandate-card">
                            <CardContent className="space-y-3 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Mandat und Beitrag</p>
                              <MetaCell label="Mandatsreferenz" value={selectedMember.billing_preferences?.mandate_reference || "nicht hinterlegt"} testId="admin-member-dossier-mandate-reference" />
                              <MetaCell label="Kontoinhaber" value={selectedMember.billing_preferences?.account_holder || "nicht hinterlegt"} testId="admin-member-dossier-mandate-holder" />
                              <MetaCell label="IBAN" value={selectedMember.billing_preferences?.iban || "nicht hinterlegt"} testId="admin-member-dossier-mandate-iban" />
                              <MetaCell label="Bankinstitut" value={selectedMember.billing_preferences?.bank_name || "nicht hinterlegt"} testId="admin-member-dossier-mandate-bank" />
                            </CardContent>
                          </Card>

                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-login-card">
                            <CardContent className="space-y-3 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Login und Zugang</p>
                              <MetaCell label="Login" value={selectedMember.email} testId="admin-member-dossier-login-email" />
                              <MetaCell label="Passwort zuletzt geändert" value={selectedMember.password_changed_at || "nicht hinterlegt"} testId="admin-member-dossier-password-changed" />
                              <MetaCell label="Zugangshinweis" value="Passwort kann per Reset-Mail neu gesetzt werden" testId="admin-member-dossier-password-note" />
                            </CardContent>
                          </Card>
                        </div>

                        {!!selectedApplications.length && (
                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-application-card">
                            <CardContent className="space-y-4 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Bewerbungsunterlagen und Aufnahmevorgang</p>
                              {selectedApplications.map((application) => (
                                <div key={application.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-member-dossier-application-${application.id}`}>
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <RecordTag value={application.id} testId={`admin-member-dossier-application-id-${application.id}`} />
                                    <StatusBadge status={application.status} testId={`admin-member-dossier-application-status-${application.id}`} />
                                  </div>
                                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{application.review_note || application.about}</p>
                                  {!!application.reference_files?.length && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {application.reference_files.map((file, index) => (
                                        <Button key={file.id} variant="outline" className="rounded-full" onClick={() => openReferenceFile(file)} data-testid={`admin-member-dossier-file-open-${application.id}-${index + 1}`}>Referenz {index + 1}</Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        )}

                        <div className="grid gap-4 lg:grid-cols-3">
                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-ticket-card">
                            <CardContent className="space-y-3 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Vorgänge</p>
                              {(selectedTickets.length ? selectedTickets : [{ id: 'none', title: 'Keine Vorgänge', status: '', message: '' }]).map((ticket) => (
                                <div key={ticket.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`admin-member-dossier-ticket-${ticket.id}`}>
                                  <p className="font-medium text-slate-900">{ticket.title}</p>
                                  {ticket.status && <div className="mt-2"><StatusBadge status={ticket.status} testId={`admin-member-dossier-ticket-status-${ticket.id}`} /></div>}
                                </div>
                              ))}
                            </CardContent>
                          </Card>

                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-email-card">
                            <CardContent className="space-y-3 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">E-Mail-Verlauf</p>
                              {(selectedEmailLogs.length ? selectedEmailLogs : [{ id: 'none', subject: 'Keine protokollierten E-Mails', status: '' }]).map((mail) => (
                                <div key={mail.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`admin-member-dossier-email-${mail.id}`}>
                                  <p className="font-medium text-slate-900">{mail.subject}</p>
                                  {mail.status && <div className="mt-2"><StatusBadge status={mail.status} testId={`admin-member-dossier-email-status-${mail.id}`} /></div>}
                                </div>
                              ))}
                            </CardContent>
                          </Card>

                          <Card className="border-slate-200 bg-white shadow-none" data-testid="admin-member-dossier-admin-history-card">
                            <CardContent className="space-y-3 p-5">
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Verwaltungsverlauf</p>
                              {((selectedMember.admin_history || []).slice().reverse().slice(0, 6)).map((entry, index) => (
                                <div key={`${selectedMember.id}-history-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`admin-member-dossier-history-${index + 1}`}>
                                  <p className="font-medium text-slate-900">{entry.action}</p>
                                  <p className="mt-1 text-xs text-slate-500">{entry.timestamp}</p>
                                  {entry.note && <p className="mt-2 text-sm text-slate-600">{entry.note}</p>}
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </section>
            )}

            {activeTab === "payments" && (
              <section className="space-y-4" data-testid="admin-payments-panel">
                <SectionBanner
                  kicker="Beitragsregister"
                  title="Zahlungsvorgänge mit klaren Kennungen und Zuständen"
                  description="Beitragsvorgänge und Lastschriftmandate erscheinen mit Kennung, Status und Bearbeitungsstand in registerartiger Leselogik."
                  testId="admin-payments-banner"
                />
                <div className="flex flex-wrap gap-3" data-testid="admin-payments-filter-bar">
                  <Button variant={paymentFilter === "all" ? "default" : "outline"} className="rounded-full" onClick={() => setPaymentFilter("all")} data-testid="admin-payments-filter-all">Alle</Button>
                  <Button variant={paymentFilter === "open" ? "default" : "outline"} className="rounded-full" onClick={() => setPaymentFilter("open")} data-testid="admin-payments-filter-open">Offene Mandate</Button>
                  <Button variant={paymentFilter === "checked" ? "default" : "outline"} className="rounded-full" onClick={() => setPaymentFilter("checked")} data-testid="admin-payments-filter-checked">Geprüft / bestätigt</Button>
                  <Button variant={paymentFilter === "released" ? "default" : "outline"} className="rounded-full" onClick={() => setPaymentFilter("released")} data-testid="admin-payments-filter-released">Abbuchung freigegeben</Button>
                </div>
                {filteredPayments.map((payment) => (
                  <Card key={payment.id} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-payment-card-${payment.id}`}>
                    <CardContent className="space-y-4 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                        <p className="font-semibold text-slate-950">{payment.email}</p>
                        <div className="mt-2">
                          <RecordTag value={payment.session_id} testId={`admin-payment-session-${payment.id}`} />
                        </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-slate-950">{payment.amount} {payment.currency?.toUpperCase()}</p>
                          <div className="mt-2 flex justify-end gap-2">
                            <StatusBadge status={payment.payment_status} testId={`admin-payment-status-pill-${payment.id}`} />
                            <StatusBadge status={payment.status} testId={`admin-payment-flow-pill-${payment.id}`} />
                          </div>
                        </div>
                      </div>
                      {payment.mandate && (
                        <div className="grid gap-3 md:grid-cols-2">
                          <MetaCell label="Kontoinhaber" value={payment.mandate.account_holder || "nicht hinterlegt"} testId={`admin-payment-holder-${payment.id}`} />
                          <MetaCell label="IBAN" value={payment.mandate.iban_masked || "nicht hinterlegt"} testId={`admin-payment-iban-${payment.id}`} />
                          <MetaCell label="BIC" value={payment.mandate.bic || "nicht hinterlegt"} testId={`admin-payment-bic-${payment.id}`} />
                          <MetaCell label="Bankinstitut" value={payment.mandate.bank_name || "nicht hinterlegt"} testId={`admin-payment-bank-${payment.id}`} />
                          {payment.mandate.account_address && <MetaCell label="Anschrift Kontoinhaber" value={payment.mandate.account_address} testId={`admin-payment-address-${payment.id}`} />}
                          <MetaCell label="Mandat bestätigt am" value={payment.mandate.confirmed_at || "nicht hinterlegt"} testId={`admin-payment-confirmed-${payment.id}`} />
                        </div>
                      )}
                      {!!payment.history?.length && (
                        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`admin-payment-history-${payment.id}`}>
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Status-Historie</p>
                          <div className="mt-3 space-y-2">
                            {payment.history.slice().reverse().slice(0, 5).map((entry, index) => (
                              <div key={`${payment.id}-history-${index}`} className="grid gap-1 text-sm text-slate-600 md:grid-cols-[1fr_1fr_1fr]" data-testid={`admin-payment-history-entry-${payment.id}-${index + 1}`}>
                                <span>{entry.status}</span>
                                <span>{entry.payment_status}</span>
                                <span className="text-slate-400">{entry.timestamp}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3" data-testid={`admin-payment-actions-${payment.id}`}>
                        <Button variant="outline" className="rounded-full" onClick={() => updateMandateStatus(payment.id, "Mandat geprüft", payment.payment_status)} data-testid={`admin-payment-check-${payment.id}`}>Mandat geprüft</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => updateMandateStatus(payment.id, "Mandat bestätigt", payment.payment_status)} data-testid={`admin-payment-confirm-${payment.id}`}>Mandat bestätigt</Button>
                        <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => updateMandateStatus(payment.id, "Abbuchung freigegeben", "freigegeben")} data-testid={`admin-payment-release-${payment.id}`}>Abbuchung freigegeben</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => downloadAdminDocument(`/admin/documents/payment/${payment.id}/mandate/pdf`, `${payment.session_id}-mandat.pdf`)} data-testid={`admin-payment-mandate-pdf-${payment.id}`}>Mandat PDF</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => downloadAdminDocument(`/admin/documents/payment/${payment.id}/sheet/html`, `${payment.session_id}-beitragsblatt.html`)} data-testid={`admin-payment-sheet-html-${payment.id}`}>Beitragsblatt HTML</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </section>
            )}

            {activeTab === "tickets" && (
              <section className="space-y-4" data-testid="admin-tickets-panel">
                <SectionBanner
                  kicker="Vorgangsregister"
                  title="Vorgänge und Verwaltungsfälle in geordneter Folge"
                  description="Anliegen, Zustände und Maßnahmen werden mit klarer Fallführung und Kennungsdarstellung zusammengeführt."
                  testId="admin-tickets-banner"
                />
                {snapshot.tickets.map((ticket) => (
                  <Card key={ticket.id} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-ticket-card-${ticket.id}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <RecordTag value={ticket.id} testId={`admin-ticket-id-${ticket.id}`} />
                          <p className="font-semibold text-slate-950">{ticket.title}</p>
                          <p className="text-sm text-slate-500">{ticket.requester_name} · {ticket.category}</p>
                        </div>
                        <StatusBadge status={ticket.status} testId={`admin-ticket-status-${ticket.id}`} />
                      </div>
                      <p className="text-sm leading-relaxed text-slate-600">{ticket.message}</p>
                      <div className="flex flex-wrap gap-3">
                        <Button variant="outline" className="rounded-full" onClick={() => updateTicket(ticket.id, "in Prüfung")} data-testid={`admin-ticket-review-${ticket.id}`}>In Prüfung</Button>
                        <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => updateTicket(ticket.id, "geschlossen")} data-testid={`admin-ticket-close-${ticket.id}`}>Schließen</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </section>
            )}

            {activeTab === "legal" && (
              <section className="space-y-4" data-testid="admin-legal-panel">
                {snapshot.legal.map((doc) => (
                  <Card key={doc.slug} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-legal-card-${doc.slug}`}>
                    <CardContent className="space-y-4 p-6">
                      <Input value={legalDrafts[doc.slug]?.title || ""} onChange={(event) => setLegalDrafts({ ...legalDrafts, [doc.slug]: { ...legalDrafts[doc.slug], title: event.target.value } })} data-testid={`admin-legal-title-${doc.slug}`} />
                      <Textarea value={legalDrafts[doc.slug]?.content || ""} onChange={(event) => setLegalDrafts({ ...legalDrafts, [doc.slug]: { ...legalDrafts[doc.slug], content: event.target.value } })} className="min-h-[180px]" data-testid={`admin-legal-content-${doc.slug}`} />
                      <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={() => saveLegal(doc.slug)} data-testid={`admin-legal-save-${doc.slug}`}>Speichern</Button>
                    </CardContent>
                  </Card>
                ))}
              </section>
            )}

            {activeTab === "help" && (
              <section className="space-y-6" data-testid="admin-help-panel">
                <SectionBanner
                  kicker="Interne Hilfeseite"
                  title="Geschäftsstelle und Prüfstelle"
                  description="Interne Zuständigkeiten, kurze Arbeitsabläufe und sachliche Dokumentationshinweise für die laufende Verbandsarbeit."
                  testId="admin-help-banner"
                />
                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid="admin-help-card">
                  <CardContent className="space-y-4 p-6">
                    <Input value={internalGuideDraft.title} onChange={(event) => setInternalGuideDraft({ ...internalGuideDraft, title: event.target.value })} data-testid="admin-help-title-input" />
                    <Textarea value={internalGuideDraft.content} onChange={(event) => setInternalGuideDraft({ ...internalGuideDraft, content: event.target.value })} className="min-h-[260px]" data-testid="admin-help-content-textarea" />
                    <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={saveInternalGuide} data-testid="admin-help-save-button">Interne Hilfe speichern</Button>
                  </CardContent>
                </Card>
              </section>
            )}

            {activeTab === "mailbox" && (
              <section className="space-y-6" data-testid="admin-mailbox-panel">
                <SectionBanner
                  kicker="Posteingang"
                  title="Empfangene Nachrichten im Überblick"
                  description="Die zuletzt im Postfach eingegangenen E-Mails werden hier mit Absender, Betreff und Lesestatus ausgewiesen."
                  testId="admin-mailbox-banner"
                />
                <div className="flex flex-wrap gap-3" data-testid="admin-mailbox-filter-bar">
                  <Button variant={mailboxFilter === "all" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("all")} data-testid="admin-mailbox-filter-all">Alle</Button>
                  <Button variant={mailboxFilter === "unread" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("unread")} data-testid="admin-mailbox-filter-unread">Ungelesen</Button>
                  <Button variant={mailboxFilter === "bounce" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("bounce")} data-testid="admin-mailbox-filter-bounce">Rückläufer</Button>
                  <Button variant={mailboxFilter === "member" ? "default" : "outline"} className="rounded-full" onClick={() => setMailboxFilter("member")} data-testid="admin-mailbox-filter-member">Mitgliedsbezogen</Button>
                </div>
                <div className="grid gap-4 md:grid-cols-4" data-testid="admin-mailbox-metrics-grid">
                  <MetaCell label="Gesamt" value={String((snapshot.mailbox_messages || []).length)} testId="admin-mailbox-metric-total" />
                  <MetaCell label="Ungelesen" value={String((snapshot.mailbox_messages || []).filter((m) => m.unread).length)} testId="admin-mailbox-metric-unread" />
                  <MetaCell label="Rückläufer" value={String((snapshot.mailbox_messages || []).filter((m) => m.mail_type === 'Rückläufer').length)} testId="admin-mailbox-metric-bounce" />
                  <MetaCell label="Mitgliedsbezogen" value={String((snapshot.mailbox_messages || []).filter((m) => ['Mitgliedsantrag','Beschwerde','Mandat','Passwort'].includes(m.mail_type)).length)} testId="admin-mailbox-metric-member" />
                </div>
                <div className="space-y-4">
                  {sortedMailboxMessages.map((mail) => (
                    <Card key={mail.id} className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm" data-testid={`admin-mailbox-card-${mail.id}`}>
                      <CardContent className="space-y-3 p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-950">{mail.subject || "Ohne Betreff"}</p>
                            <p className="text-sm text-slate-500">{mail.from}</p>
                            {mail.preview && <p className="mt-2 text-sm leading-relaxed text-slate-600">{mail.preview}</p>}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <StatusBadge status={mail.mail_type} testId={`admin-mailbox-type-pill-${mail.id}`} className={mail.mail_type === 'Rückläufer' ? 'border-rose-200 bg-rose-50 text-rose-800' : ''} />
                            <StatusBadge status={mail.unread ? "ungelesen" : "gelesen"} testId={`admin-mailbox-pill-${mail.id}`} />
                            {mail.admin_status && <StatusBadge status={mail.admin_status} testId={`admin-mailbox-admin-pill-${mail.id}`} />}
                          </div>
                        </div>
                        <MetaCell label="Eingang" value={mail.date || "nicht verfügbar"} testId={`admin-mailbox-date-${mail.id}`} />
                        <div className="grid gap-3 md:grid-cols-[220px_1fr_auto]" data-testid={`admin-mailbox-actions-${mail.id}`}>
                          <select
                            value={mailboxDrafts[mail.id]?.admin_status ?? mail.admin_status ?? ""}
                            onChange={(event) => setMailboxDrafts({ ...mailboxDrafts, [mail.id]: { ...(mailboxDrafts[mail.id] || { note: mail.admin_note || "" }), admin_status: event.target.value } })}
                            className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900"
                            data-testid={`admin-mailbox-admin-status-select-${mail.id}`}
                          >
                            <option value="">Kein interner Vermerk</option>
                            <option value="in Bearbeitung">in Bearbeitung</option>
                            <option value="beantwortet">beantwortet</option>
                            <option value="abgeschlossen">abgeschlossen</option>
                          </select>
                          <Input
                            value={mailboxDrafts[mail.id]?.note ?? mail.admin_note ?? ""}
                            onChange={(event) => setMailboxDrafts({ ...mailboxDrafts, [mail.id]: { ...(mailboxDrafts[mail.id] || { admin_status: mail.admin_status || "" }), note: event.target.value } })}
                            placeholder="Interne Notiz"
                            data-testid={`admin-mailbox-note-input-${mail.id}`}
                          />
                          <Button variant="outline" className="rounded-full" onClick={() => saveMailboxAnnotation(mail.id)} data-testid={`admin-mailbox-save-${mail.id}`}>Speichern</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {activeTab === "content" && (
              <section className="space-y-6" data-testid="admin-content-panel">
                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">SEO & öffentliche Verbandstexte</h2>
                    <Input value={settingsDraft.seo.title} onChange={(event) => setSettingsDraft({ ...settingsDraft, seo: { ...settingsDraft.seo, title: event.target.value } })} data-testid="admin-settings-seo-title-input" />
                    <Textarea value={settingsDraft.seo.description} onChange={(event) => setSettingsDraft({ ...settingsDraft, seo: { ...settingsDraft.seo, description: event.target.value } })} data-testid="admin-settings-seo-description-textarea" />
                    <Input value={settingsDraft.association_profile.headline} onChange={(event) => setSettingsDraft({ ...settingsDraft, association_profile: { ...settingsDraft.association_profile, headline: event.target.value } })} data-testid="admin-settings-headline-input" />
                    <Textarea value={settingsDraft.association_profile.subheadline} onChange={(event) => setSettingsDraft({ ...settingsDraft, association_profile: { ...settingsDraft.association_profile, subheadline: event.target.value } })} data-testid="admin-settings-subheadline-textarea" />
                    <Textarea value={settingsDraft.association_profile.membership_pitch} onChange={(event) => setSettingsDraft({ ...settingsDraft, association_profile: { ...settingsDraft.association_profile, membership_pitch: event.target.value } })} data-testid="admin-settings-membership-pitch-textarea" />
                    <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={saveSettings} data-testid="admin-settings-save-button">Einstellungen speichern</Button>
                  </CardContent>
                </Card>

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Freischaltbare Verbandsmodule</h2>
                    {Object.entries(settingsDraft.modules).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`admin-module-row-${key}`}>
                        <div>
                          <p className="font-semibold text-slate-950">{key.replace(/_/g, " ")}</p>
                        </div>
                        <Switch checked={value} onCheckedChange={(checked) => setSettingsDraft({ ...settingsDraft, modules: { ...settingsDraft.modules, [key]: checked } })} data-testid={`admin-module-switch-${key}`} />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <div className="flex items-center justify-between gap-4">
                      <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Weiterbildungsangebot anlegen</h2>
                      <Button variant="outline" className="rounded-full" onClick={refreshNews} data-testid="admin-content-refresh-news-button">KI-News neu laden</Button>
                    </div>
                    <Input value={seminarForm.title} onChange={(event) => setSeminarForm({ ...seminarForm, title: event.target.value })} placeholder="Seminartitel" data-testid="admin-seminar-title-input" />
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input value={seminarForm.category} onChange={(event) => setSeminarForm({ ...seminarForm, category: event.target.value })} placeholder="Kategorie" data-testid="admin-seminar-category-input" />
                      <Input value={seminarForm.hours} onChange={(event) => setSeminarForm({ ...seminarForm, hours: event.target.value })} placeholder="Stunden" data-testid="admin-seminar-hours-input" />
                      <Input value={seminarForm.starts_at} onChange={(event) => setSeminarForm({ ...seminarForm, starts_at: event.target.value })} placeholder="Startdatum ISO" data-testid="admin-seminar-starts-at-input" />
                      <Input value={seminarForm.venue} onChange={(event) => setSeminarForm({ ...seminarForm, venue: event.target.value })} placeholder="Ort" data-testid="admin-seminar-venue-input" />
                      <Input value={seminarForm.speaker} onChange={(event) => setSeminarForm({ ...seminarForm, speaker: event.target.value })} placeholder="Referent" data-testid="admin-seminar-speaker-input" />
                    </div>
                    <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={createSeminar} data-testid="admin-seminar-create-button">Seminar speichern</Button>
                  </CardContent>
                </Card>

                <Card className="border-[#d4af37]/15 bg-[#fbf7ef] shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Rundschreiben und Vorlagen</h2>
                    {snapshot.newsletters.map((template) => (
                      <div key={template.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`admin-newsletter-template-${template.id}`}>
                        <p className="font-semibold text-slate-950">{template.name}</p>
                        <p className="mt-1 text-sm text-slate-500">{template.subject}</p>
                        <p className="mt-2 text-sm text-slate-600">{template.body}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
}
